import { memo, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import cn from 'classnames';

import { Icon } from 'components';
import Avatar from 'components/Avatar';
import { useOnClickOutside } from 'hooks/useOnClickOutside';
import { CommitteeAttributes } from 'lib/models/committee';

import styles from './MemberCard.module.scss';

interface MemberCardProps {
  userData: CommitteeAttributes;
  isUser: boolean;
  onEditClick: () => void;
  onRemoveClick: () => void;
}

export const MemberCard: React.FC<MemberCardProps> = memo(
  ({ userData, onEditClick, onRemoveClick, isUser }: MemberCardProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [t] = useTranslation('committee');

    const menuRef = useRef(null);
    const buttonRef = useRef(null);

    useOnClickOutside({
      ref: menuRef,
      onOutsideClick: () => setIsOpen(!isOpen),
    });

    useOnClickOutside({
      ref: buttonRef,
      onInsideClick: () => setIsOpen(!isOpen),
      onOutsideClick: () => {},
    });

    return (
      <div className={styles.card}>
        <Avatar
          icon={userData.avatar_url}
          name={userData.name}
          size="xxlarge"
        />
        {!isUser ? (
          <>
            <div className={styles.iconButton}>
              <Icon
                iconName="icon_ellipsis"
                className={styles.icon}
                size="mediumplus"
              />
              <button
                className={cn(styles.btn, {
                  [styles.clicked]: isOpen,
                })}
                ref={buttonRef}
              />
            </div>
            {isOpen && (
              <div className={styles.dropdownItems} ref={menuRef}>
                <button
                  onClick={() => {
                    onEditClick();
                    setIsOpen(!isOpen);
                  }}
                  className={styles.dropdownItem}
                >
                  {t('remove-member-modal.edit-button')}
                </button>
                <button
                  onClick={() => {
                    onRemoveClick();
                    setIsOpen(!isOpen);
                  }}
                  className={cn(styles.dropdownItem, styles['remove-button'])}
                >
                  {t('remove-member-modal.remove-button')}
                </button>
              </div>
            )}
          </>
        ) : (
          <div className={styles.iconButton}>
            <Icon
              iconName="icon_pencil"
              className={styles.icon}
              size="mediumplus"
            />
            <button className={cn(styles.btn)} onClick={onEditClick} />
          </div>
        )}
        <div>
          <h3 className={styles.title}>{userData.name}</h3>
          <p className={styles.description}>{userData.role}</p>
        </div>
      </div>
    );
  }
);

MemberCard.displayName = 'MemberCard';
